import type { FunctionComponent } from "react"

import { clsx } from "clsx"
import dynamic from "next/dynamic"
import { useEffect, useState } from "react"

import { Button } from "@jobteaser/spark/components/Button"
import { Link } from "@jobteaser/spark/components/Icons/Link"
import { StarFilled } from "@jobteaser/spark/components/Icons/StarFilled"
import { StarOutlined } from "@jobteaser/spark/components/Icons/StarOutlined"

import { NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS } from "@/modules/careerCenter/constants/flags"
import { useCareerCenterFetch } from "@/modules/careerCenter/hooks/useCareerCenterFetch"
import { isWwwCareerCenter } from "@/modules/careerCenter/isWwwCareerCenter"
import { MEDIA_TYPE_IMAGE } from "@/modules/companies/client/constants/enums"
import { useCompanyExternalLinkTrack } from "@/modules/companies/client/hooks/tracking/useCompanyExternalLinkTrack"
import { useCompanyFollowTrack } from "@/modules/companies/client/hooks/tracking/useCompanyFollowTrack"
import { useCompanyFetch } from "@/modules/companies/client/hooks/useCompanyFetch"
import { useCompanyFollowerFetch } from "@/modules/companies/client/hooks/useCompanyFollowerFetch"
import { useFollowCompany } from "@/modules/companies/client/hooks/useFollowCompany"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useLocale } from "@/modules/locales/useLocale"
import { formatNumber } from "@/modules/number/formatNumber"
import { useFlag } from "@/modules/unleash/hooks/useFlag"
import { useCurrentUserFetch } from "@/modules/user/client/hooks/useCurrentUserFetch"

import styles from "./HeaderActions.module.css"

const MIN_UNLOGGED_FOLLOWERS_SHOWN = 200

const DynamicLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/LoginModal").then(mod => mod.LoginModal),
  { ssr: false }
)

const DynamicNewLoginModal = dynamic(
  () => import("@/modules/authentication/components/LoginModal/NewLoginModal").then(mod => mod.NewLoginModal),
  { ssr: false }
)

export const HeaderActions: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const companyFollowTrack = useCompanyFollowTrack()
  const companyExternalLinkTrack = useCompanyExternalLinkTrack()
  const { dataCurrentUser } = useCurrentUserFetch()
  const { dataCompany } = useCompanyFetch()
  const { dataCompanyFollower } = useCompanyFollowerFetch()
  const { followCompany, isLoadingFollowCompany } = useFollowCompany()
  const { dataCareerCenter } = useCareerCenterFetch()
  const [isDisplayedButtonText, setIsDisplayedButtonText] = useState(false)
  const [isLoginFlowOpen, setIsLoginFlowOpen] = useState(false)
  const isNewSignInFlowEnabled = useFlag(NEW_SIGN_IN_FLOW, NEW_SIGN_IN_FLOW_VARIANTS.ENABLED)

  const isHeaderWithGallery =
    dataCompany && dataCompany.page.gallery.some(currentMedia => currentMedia.type === MEDIA_TYPE_IMAGE)
  const isUnlogged = !dataCurrentUser?.isLogged

  const websiteUrl = dataCompany?.page.website

  const isFollowingCompany = !!dataCompanyFollower?.userInfo?.followsCompany
  const isAuthorizedToFollow = !isUnlogged && !!dataCompanyFollower?.userInfo?.authorizedToFollow
  const followersNumber = dataCompanyFollower?.followers || 0
  const hasMinimumNumberOfFollowers = followersNumber > MIN_UNLOGGED_FOLLOWERS_SHOWN
  const followButtonText = isFollowingCompany
    ? t("companies_client.header.followButtonAlready")
    : t("companies_client.header.followButton")

  useEffect(() => {
    const displayButtonTextAboutScreenSize = (): void => {
      const windowWidth = window.innerWidth
      const availableWindowSizes = {
        large: 1024,
        medium: 768,
        small: 480,
      }

      if (windowWidth >= availableWindowSizes.small) {
        setIsDisplayedButtonText(true)
      }

      if (
        isHeaderWithGallery &&
        windowWidth >= availableWindowSizes.medium &&
        windowWidth < availableWindowSizes.large
      ) {
        setIsDisplayedButtonText(false)
      }

      if (isHeaderWithGallery && windowWidth >= availableWindowSizes.large) {
        setIsDisplayedButtonText(true)
      }
    }

    displayButtonTextAboutScreenSize()
  }, [isHeaderWithGallery])

  const onFollowLogged = (): void => {
    if (!isLoadingFollowCompany) {
      companyFollowTrack(!isFollowingCompany)
      followCompany(!isFollowingCompany)
    }
  }

  const onFollowUnlogged = (): void => {
    companyFollowTrack(false)
    setIsLoginFlowOpen(true)
  }

  return (
    <div className={clsx(styles.headerActions, { [styles["headerActions--withGallery"]]: isHeaderWithGallery })}>
      {isAuthorizedToFollow && (
        <Button
          icon={isFollowingCompany ? StarFilled : StarOutlined}
          className={styles.button}
          onClick={onFollowLogged}
          title={followButtonText}
          data-testid="company_header_follow"
          aria-label={!isDisplayedButtonText ? followButtonText : undefined}
        >
          {isDisplayedButtonText && <span>{followButtonText}</span>}
          <span data-testid="company_header_followers">{` (${formatNumber(followersNumber, locale, true)})`}</span>
        </Button>
      )}
      {isUnlogged && (
        <>
          {/* Login Flow */}
          {isLoginFlowOpen && isNewSignInFlowEnabled && (
            <DynamicNewLoginModal
              careerCenterName={dataCareerCenter?.name || ""}
              isWwwCareerCenter={isWwwCareerCenter(dataCareerCenter)}
              onClose={() => setIsLoginFlowOpen(false)}
            />
          )}
          {isLoginFlowOpen && !isNewSignInFlowEnabled && (
            <DynamicLoginModal
              careerCenterName={dataCareerCenter?.name || ""}
              isWwwCareerCenter={isWwwCareerCenter(dataCareerCenter)}
              onClose={() => setIsLoginFlowOpen(false)}
            />
          )}

          <Button
            icon={StarOutlined}
            className={styles.button}
            onClick={onFollowUnlogged}
            title={t("companies_client.header.followButton")}
            data-testid="company_header_follow_unlogged"
            aria-label={!isDisplayedButtonText ? t("companies_client.header.followButton") : undefined}
          >
            {isDisplayedButtonText && <span>{t("companies_client.header.followButton")}</span>}
            {hasMinimumNumberOfFollowers ? (
              <span data-testid="company_header_followers_unlogged">
                &nbsp;{`(${formatNumber(followersNumber, locale, true)})`}
              </span>
            ) : null}
          </Button>
        </>
      )}
      {websiteUrl && (
        <Button
          renderComponent="a"
          href={websiteUrl}
          target="_blank"
          rel="nofollow noopener"
          icon={Link}
          variant="secondary"
          className={styles.button}
          onClick={() => companyExternalLinkTrack(websiteUrl, "corporate")}
          title={t("companies_client.header.gotToWebsite")}
          data-testid="company_header_website_link"
          aria-label={
            !isDisplayedButtonText
              ? `${t("companies_client.header.gotToWebsite")} ${t("companies_client.link.target_blank")}`
              : undefined
          }
        >
          {isDisplayedButtonText && <span>{t("companies_client.header.gotToWebsite")}</span>}
          <span className="sr-only">{t("companies_client.link.target_blank")}</span>
        </Button>
      )}
    </div>
  )
}
